import { Card } from "@rumbledirect/ui";
import { Image, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useCallback, useState } from "react";
import {
  Card as V2Card,
  CardContent,
  CardHeader,
  CardTitle,
  Image as V2Image,
  Lightbox,
} from "@rumbledirect/ui-v2";
import { MotionProvider } from "@rumbledirect/ui-v2/motion";

export interface MediaGalleryProps {
  title: string;
  uiV2?: boolean;
  items: Array<{
    url: string;
    name: string;
    mime: string;
  }>;
}

const V2MediaGallery = ({ title, items }: MediaGalleryProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // The Lightbox portals out of the card; render it inside the ViewRenderer's
  // .ui-v2-theme wrapper so the scoped tokens and affiliate theme still apply.
  const [themeRoot, setThemeRoot] = useState<HTMLElement | null>(null);
  const cardRef = useCallback((card: HTMLDivElement | null) => {
    setThemeRoot(card?.closest<HTMLElement>(".ui-v2-theme") ?? null);
  }, []);

  const imageItems = items.filter((item) => item.mime.startsWith("image/"));
  const fileItems = items.filter((item) => !item.mime.startsWith("image/"));

  return (
    <V2Card ref={cardRef}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {imageItems.length > 0 && (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
            {imageItems.map((item, index) => (
              <li key={item.url}>
                <button
                  type="button"
                  aria-label={`${item.name} vergroten`}
                  className="block w-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setLightboxIndex(index)}
                >
                  <V2Image src={item.url} alt={item.name} ratio="1" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {fileItems.length > 0 && (
          <ul className="space-y-2">
            {fileItems.map((item) => (
              <li key={item.url}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <MotionProvider>
        <Lightbox
          open={lightboxIndex !== null}
          onOpenChange={(open) => {
            if (!open) setLightboxIndex(null);
          }}
          items={imageItems.map((item) => ({ src: item.url, alt: item.name }))}
          index={lightboxIndex ?? 0}
          container={themeRoot}
        />
      </MotionProvider>
    </V2Card>
  );
};

export const MediaGallery = ({ title, items, uiV2 }: MediaGalleryProps) => {
  if (items.length === 0) {
    return null;
  }

  if (uiV2) {
    return <V2MediaGallery title={title} items={items} />;
  }

  const imageItems = items.filter((item) => item.mime.startsWith("image/"));
  const fileItems = items.filter((item) => !item.mime.startsWith("image/"));

  return (
    <Card title={title}>
      {imageItems.length > 0 && (
        <Image.PreviewGroup>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 8,
            }}
          >
            {imageItems.map((item) => (
              <Image
                key={item.url}
                src={item.url}
                alt={item.name}
                style={{
                  width: "100%",
                  aspectRatio: "1",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      )}
      {fileItems.length > 0 && (
        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
          {fileItems.map((item) => (
            <a
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <DownloadOutlined />
              <span>{item.name}</span>
            </a>
          ))}
        </Space>
      )}
    </Card>
  );
};
